import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSession from "Hooks/session.hook";
import { pick, omit, omitBy, isNil, isEmpty } from "lodash";
import { listBoxes } from "Services/delivery.services";
import { fetchOrderList } from "Services/order.service";
import { Radio } from "antd";
import { SortingMode } from "../../../types/common.types";
import { toaster, toNum } from "Helpers/index";
import CompletedBoxesList from "Components/delivery-management/parcel-tracking-tabs/completed-tabs/completed-boxes-list";
import CompletedParcelsList from "Components/delivery-management/parcel-tracking-tabs/completed-tabs/completed-parcels-list";
import CompletedCustomerList from "./completed-customer-list";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";

interface CompletedListProps {
  sortingHubOptions: any[];
  queryParams: any;
  itemPerPage: number;
}

const CompletedList: React.FC<CompletedListProps> = ({
  sortingHubOptions,
  queryParams,
  itemPerPage,
}) => {
  const { org, roles, roleTypes } = useSession();
  const { t } = useTranslation();
  const isRoleCustomer = roles[0].type == "CUSTOMER" ? true : false;
  const globalFilterState = useRecoilValue(GlobalFilterAtom);
  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [radioButtonState, setRadioButtonState] = useState("0");

  let initialFilters = {
    orgID: [org._id],
    status: ["DELIVERED", "CLOSED"],
    customerOrgIDs: isRoleCustomer ? [org._id] : null,
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };

  const [refreshPayload, setRefreshPayload] = useState<any>(initialFilters);

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      handleAPICalls(initialFilters);
      return;
    }
    let filterValue = {
      orgID: filterInput.customersList
        ? [filterInput.customersList]
        : [org._id],
      orFields: filterInput.orFields || null,
      sortingHubID: filterInput.sortingHub || null,
      deliveryType: filterInput.deliveryType || null,
      status: filterInput.status
        ? typeof filterInput.status != "object"
          ? [filterInput.status]
          : filterInput.status
        : initialFilters.status,
      handoverPointID: filterInput.destination || null,
    };

    setRefreshPayload(filterValue);
    handleAPICalls(filterValue);
  };

  const handleAPICalls = (payload = {}) => {
    switch (radioButtonState) {
      case "0":
        handleListingBoxes(payload);
        break;
      case "1":
        handleListingParcelOrders(payload);
        break;
    }
  };

  const handleListingBoxes = async (payload: any) => {
    let filters = pick(payload, [
      "customerOrgIDs",
      "status",
      "sortingHubID",
      "orFields",
      "ancestorOrgIDs",
    ]);

    const responseData = await listBoxes(
      omitBy(filters, isNil),
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "status", mode: SortingMode.DESC },
    );
    if (responseData && !responseData.error) {
      setAllOrderList(responseData);
    } else {
      toaster("error", responseData.error);
    }
  };

  const handleListingParcelOrders = async (payload: any) => {
    let filters: any = omitBy(
      {
        ...omit(payload, ["status"]),
        orderStatus: payload.status ? payload.status : initialFilters.status,
      },
      isNil,
    );

    const responseData = await fetchOrderList(
      filters,
      { offset: queryParams.offset, limit: toNum(itemPerPage) },
      { sortBy: "orderStatus", mode: "DESC" },
    );
    if (responseData && !responseData.error) {
      setAllOrderList(responseData);
    } else {
      toaster("error", t("error.badRequest"));
    }
  };

  const tabs = useMemo(
    () => [
      {
        key: "0",
        component: (
          <CompletedBoxesList
            sortingHubOptions={sortingHubOptions}
            parcelList={allOrderList}
            onFilter={onFilter}
            initialStatus={initialFilters.status}
          />
        ),
      },
      {
        key: "1",
        component: (
          <CompletedParcelsList
            sortingHubOptions={sortingHubOptions}
            parcelList={allOrderList}
            onFilter={onFilter}
          />
        ),
      },
    ],
    [allOrderList, radioButtonState, sortingHubOptions],
  );

  useEffect(() => {
    if (
      !isEmpty(globalFilterState) &&
      globalFilterState?.selectedOrgIDs?.length
    ) {
      onFilter({
        ...refreshPayload,
        ancestorOrgIDs: globalFilterState?.selectedOrgIDs,
      });
    }
  }, [globalFilterState]);

  return (
    <div className="parcel-order-list">
      {roleTypes == "CUSTOMER-SUPER_ADMIN" ? (
        <CompletedCustomerList
          parcelList={allOrderList}
          onFilter={onFilter}
          initialStatus={initialFilters.status}
        />
      ) : (
        <div>
          <Radio.Group
            className="m-20 mb-10"
            buttonStyle="solid"
            value={radioButtonState}
            onChange={(event) => {
              event.preventDefault();
              setRadioButtonState(event.target.value);
            }}
          >
            <Radio.Button value="0">{t("option.boxes")}</Radio.Button>
            <Radio.Button value="1">{t("option.parcels")}</Radio.Button>
          </Radio.Group>
          <ErrorBoundary>
            <div>{tabs[radioButtonState].component}</div>
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export default CompletedList;
